import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { useAuth } from "../../context/auth";
import { useTheme } from "../../context/theme";
import { useLocale } from "../../context/locale";
import { LOCALE_LABELS, LOCALES } from "../../i18n";
import type { TranslationKey } from "../../i18n";
import { api } from "../../lib/api";
import { useDuration } from "../../lib/use-relative";
import { BrandLockup } from "../brand";
import { Badge, Button, cx } from "../ui";
import { AUDIT_ITEM, NAV_GROUPS } from "./nav";
import type { NavItem } from "./nav";
import type { SystemStats } from "../../types/api";

const PAGE_TITLES: Record<string, TranslationKey> = {
  "/": "nav.dashboard",
  "/draws": "nav.draws",
  "/games": "nav.games",
  "/review": "nav.review",
  "/players": "nav.players",
  "/test-players": "nav.testPlayers",
  "/admins": "nav.admins",
  "/sessions": "nav.sessions",
  "/bets": "nav.bets",
  "/ledger": "nav.ledger",
  "/reports": "nav.reports",
  "/referrals": "nav.referrals",
  "/notifications": "nav.notifications",
  "/settings": "nav.settings",
  "/audit": "nav.audit",
};

function titleKeyFor(pathname: string): TranslationKey {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (pathname.startsWith("/players/")) return "nav.players";
  if (pathname.startsWith("/draws/")) return "nav.draws";
  return "app.name";
}

export function Shell() {
  const { admin, signOut, can, allowed, sessionExpiresIn, expiringSoon } = useAuth();
  const { theme, toggle } = useTheme();
  const { locale, setLocale, t } = useLocale();
  const duration = useDuration();
  const location = useLocation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [system, setSystem] = useState<SystemStats | null>(null);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api
        .system()
        .then((stats) => !cancelled && setSystem(stats))
        .catch(() => undefined);
    };
    load();
    const timer = window.setInterval(load, 30_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [location.pathname]);

  /**
   * Whether this operator gets to see a menu item.
   *
   * `minRole` for the two structurally superadmin-only screens, and `module`
   * for everything else — checked against the grid the API sends, so a
   * superadmin changing what a role may do changes that role's menu on the
   * next push, without anybody reloading.
   */
  const visible = (item: NavItem) => {
    if (item.minRole && !can(item.minRole)) return false;
    if (item.module && !allowed(item.module)) return false;
    return true;
  };

  return (
    <div className="bg-app min-h-dvh">
      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <aside
        className={cx(
          "fixed inset-y-0 left-0 z-40 flex w-[264px] flex-col border-r-2 transition-transform duration-200",
          drawerOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
        style={{ background: "var(--bg-deep)", borderColor: "var(--border)" }}
      >
        <div
          className="flex h-16 shrink-0 items-center border-b-2 px-5"
          style={{ borderColor: "var(--border)" }}
        >
          <BrandLockup subtitle={t("app.tagline")} />
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {NAV_GROUPS.map((group) => {
            const items = group.items.filter(visible);
            if (!items.length) return null;
            return (
              <div key={t(group.label)} className="mb-5">
                <p
                  className="px-2 pb-2 text-[11px] font-bold tracking-[0.16em] uppercase"
                  style={{ color: "var(--accent)" }}
                >
                  {t(group.label)}
                </p>
                {items.map((item) => (
                  <NavItemLink key={item.to} item={item} />
                ))}
              </div>
            );
          })}

          <div
            className="mt-auto border-t pt-4"
            style={{ borderColor: "var(--border)" }}
          >
            <NavItemLink item={AUDIT_ITEM} />
          </div>
        </nav>

        <div
          className="shrink-0 border-t p-3"
          style={{ borderColor: "var(--border)" }}
        >
          <div
            className="flex items-center gap-2.5 rounded-lg p-2"
            style={{ background: "var(--surface-inset)" }}
          >
            <span
              className="grid size-8 shrink-0 place-items-center rounded-full text-[12px] font-bold uppercase"
              style={{
                background: "var(--color-crimson)",
                color: "var(--accent)",
              }}
              aria-hidden
            >
              {admin?.username.slice(0, 2)}
            </span>
            <div className="min-w-0 flex-1 leading-tight">
              <p className="truncate text-[13px] font-bold">
                {admin?.displayName ?? admin?.username}
              </p>
              <p className="eyebrow mt-0.5">
                {admin ? t(`admins.role.${admin.role}` as TranslationKey) : ""}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {drawerOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-30 lg:hidden"
          style={{ background: "rgb(0 0 0 / 0.6)" }}
          onClick={() => setDrawerOpen(false)}
          aria-label={t("app.closeNav")}
        />
      ) : null}

      {/* ── Main column ─────────────────────────────────────────────────── */}
      <div className="flex min-h-dvh flex-col lg:pl-[264px]">
        <header
          className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b px-4 backdrop-blur"
          style={{
            background: "color-mix(in srgb, var(--bg) 88%, transparent)",
            borderColor: "var(--border)",
          }}
        >
          <button
            type="button"
            className="text-muted grid size-9 place-items-center rounded-lg border lg:hidden"
            style={{ borderColor: "var(--border)" }}
            onClick={() => setDrawerOpen(true)}
            aria-label={t("app.openNav")}
          >
            ☰
          </button>

          <h1 className="display truncate text-[19px]">
            {t(titleKeyFor(location.pathname))}
          </h1>

          <div className="ml-auto flex items-center gap-2">
            {/* Only surfaced near the end of the session — a permanent clock
                would be noise on an 8-hour token. */}
            {expiringSoon && sessionExpiresIn !== null ? (
              <Badge tone="danger">
                {t("app.sessionEnds", {
                  minutes: Math.max(1, Math.round(sessionExpiresIn / 60_000)),
                })}
              </Badge>
            ) : null}

            {/* Language sits beside the theme toggle: both are personal display
                preferences, and an operator looks for them in the same place. */}
            <div
              className="flex items-center overflow-hidden rounded-lg border"
              style={{
                borderColor: "var(--border)",
                background: "var(--surface)",
              }}
              role="group"
              aria-label={t("app.language")}
            >
              {LOCALES.map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setLocale(code)}
                  className="h-9 px-2.5 text-[12px] font-semibold transition-colors"
                  style={
                    locale === code
                      ? {
                          background: "var(--color-crimson)",
                          color: "var(--color-gold-light)",
                        }
                      : { color: "var(--text-muted)" }
                  }
                  aria-pressed={locale === code}
                >
                  {LOCALE_LABELS[code]}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={toggle}
              className="text-muted grid size-9 place-items-center rounded-lg border transition-colors hover:text-[var(--accent)]"
              style={{
                borderColor: "var(--border)",
                background: "var(--surface)",
              }}
              aria-label={
                theme === "dark"
                  ? t("app.theme.toLight")
                  : t("app.theme.toDark")
              }
              title={
                theme === "dark"
                  ? t("app.theme.toLight")
                  : t("app.theme.toDark")
              }
            >
              {theme === "dark" ? "☀" : "☾"}
            </button>

            <Button
              size="sm"
              variant="secondary"
              onClick={() => signOut("manual")}
            >
              {t("app.signOut")}
            </Button>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6">
          <Outlet />
        </main>

        <footer
          className="mt-auto flex flex-wrap items-center gap-x-5 gap-y-1 border-t px-5 py-3"
          style={{ borderColor: "var(--border)" }}
        >
          <p className="eyebrow">{t("app.footer.version")}</p>
          <p className="eyebrow"></p>
          {system ? (
            <>
              <p className="eyebrow">
                {t(
                  system.realtime.connections === 1
                    ? "app.footer.connections"
                    : "app.footer.connections_plural",
                  { count: system.realtime.connections },
                )}
              </p>
              <p className="eyebrow">
                {t("app.footer.uptime", {
                  value: duration(system.uptimeSeconds),
                })}
              </p>
            </>
          ) : null}
          <p className="eyebrow ml-auto" style={{ color: "var(--accent)" }}>
            {t("app.footer.demo")}
          </p>
        </footer>
      </div>
    </div>
  );
}

function NavItemLink({ item }: { item: NavItem }) {
  const { t } = useLocale();

  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cx(
          "mb-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] font-semibold transition-colors",
          isActive ? "" : "hover:bg-[var(--surface-hover)]",
        )
      }
      style={({ isActive }) =>
        isActive
          ? {
              // The pill is crimson in both themes, so the label is light in
              // both — resolving it per theme made it dark-on-dark in light.
              background:
                "linear-gradient(90deg, var(--color-crimson), transparent 92%)",
              color: "var(--color-gold-light)",
              boxShadow: "inset 3px 0 0 var(--color-gold)",
            }
          : { color: "var(--text-muted)" }
      }
    >
      <span className="w-5 shrink-0 text-center text-[15px]" aria-hidden>
        {item.icon}
      </span>
      {t(item.label)}
    </NavLink>
  );
}
